// Basic
import { useMemo, useState } from 'react';
// Store & actions
// Components
import Page from '@/common-utils/components/structure/page/Page';
import SimCardsTable from '@/app/modules/sims/components/tables/SimCardsTable';
import SimGroupsTable from '@/app/modules/sims/components/tables/SimGroupsTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common-utils/components/ui/tabs';
// Common
import RouteUrls from '../../enums/RouteUrls';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import { nsSimCard } from '@/app/i18n/namespaces';
import Permissions from '@/app/permissions/Permissions';
import useSessionStore from '@/app/store/session';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import useSimsStore from '@/app/store/modules/sims';
import CustomPayModalAddSimCards from '@/app/modules/sims/components/modals/addSimCard/CustomPayModalAddSimCard';
import ICONS from '@/common-utils/enums/icons';
import CustomPayModalAssignSimCards from '@/app/modules/sims/components/modals/assign/assignSimCards/CustomPayModalAssignSimCards';
import CustomPayModalReassignSimCards from '@/app/modules/sims/components/modals/assign/reassignSimCards/CustomPayModalReassignSimCards';
import CustomPayModalUnassignSimCards from '@/app/modules/sims/components/modals/assign/unassignSimCards/CustomPayModalUnassignSimCards';
import CustomPayModalSimsMultiActivation from '@/app/modules/sims/components/modals/multipleActivation/CustomPayModalSimsMultiActivation';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
import SIM_CARDS_TABS from '../../enums/tabs/SimCardsTabs';
import actionsSims from '@/app/modules/sims/api/actionsSims';
import CUSTOM_PAY_SIM_OFFERS_PROVIDERS from '@/app/modules/sims/enums/CustomPaySimOffersProviders';
import useTabManager from '../../hooks/useTabManager';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';

/**
 * @returns {JSX} SimCards
 */
function CustomPaySimCards() {

    // URL Params
    const navigate = useNavigate();
    const location = useLocation();
    const { view } = extractQueriesFromUrl(location.search);

    // Store
    const session = useSessionStore(s => s.session);
    const permissions = session.permissions;
    const storeRefetchSimCards = useSimsStore(s => s.storeRefetchSimCards);

    // State
    const [activeTab, setActiveTab] = useState(Object.values(SIM_CARDS_TABS).find(tab => tab.name === view)?.name ?? SIM_CARDS_TABS.DEFAULT.name);
    const [showModalAddSimCards, setShowModalAddSimCards] = useState(false);
    const [showModalAssignSimCards, setShowModalAssignSimCards] = useState(false);
    const [showModalReassignSimCards, setShowModalReassignSimCards] = useState(false);
    const [showModalUnassignSimCards, setShowModalUnassignSimCards] = useState(false);
    const [showModalActivateSimCards, setShowModalActivateSimCards] = useState(false);

    // API Calls get Sim Providers
    const { data, isLoading } = actionsSims.getSimProviders();

    const simProviders = useMemo(() => {
        return data?.simProviders?.map((provider) => {
            return {
                name: CUSTOM_PAY_SIM_OFFERS_PROVIDERS[provider.providerName]?.name,
                value: CUSTOM_PAY_SIM_OFFERS_PROVIDERS[provider.providerName]?.key
            };
        });
    }, [data?.simProviders]);



    // Hooks
    // Tab Manager
    useTabManager({
        pathname: location.pathname,
        view: view,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        tabs: SIM_CARDS_TABS,
        navigate: navigate
    });

    return (
        <Page>
            <Tabs defaultValue={activeTab} onValueChange={setActiveTab}>
                <Page.Header
                    route={RouteUrls.SIM_CARDS}
                    routeBase={RouteUrls.SIM_CARDS}
                >
                    <TabsList>
                        <TabsTrigger tab={SIM_CARDS_TABS.SIM_CARDS} activeTab={activeTab} permissions={permissions}>
                            <Entity entity='page.sim_cards.tabs.sims' />
                        </TabsTrigger>
                        <TabsTrigger tab={SIM_CARDS_TABS.SIM_GROUPS} activeTab={activeTab} permissions={permissions}>
                            <Entity entity='page.sim_cards.tabs.simPools' />
                        </TabsTrigger>
                    </TabsList>
                </Page.Header>
                <Page.Body dataCy='page-sim-cards'>
                    <TabsContent tab={SIM_CARDS_TABS.SIM_CARDS} permissions={permissions}>
                        <SimCardsTable
                            filtersEventName={setFiltersEventName('sim_cards')}
                            pageActions={
                                <PageActions actions={[
                                    {
                                        icon: ICONS.USER_ADD,
                                        entity: 'actions.row.assign',
                                        namespace: nsSimCard,
                                        onClick: () => setShowModalAssignSimCards(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [
                                                Permissions.SIM_GET_STATUS_FROM_ICCID.name,
                                                Permissions.SIM_PAYVIEW_OFFER_LIST.name,
                                                Permissions.SIM_ASSOCIATE.name
                                            ]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    {
                                        icon: ICONS.ADD,
                                        entity: 'actions.row.add_to.title',
                                        namespace: nsSimCard,
                                        onClick: () => setShowModalAddSimCards(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [
                                                Permissions.SIM_IMPORT_FROM_ORDER.name
                                            ]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    {
                                        icon: ICONS.POS_TERMINAL_ACTIVATE,
                                        entity: 'actions.row.activate',
                                        namespace: nsSimCard,
                                        onClick: () => setShowModalActivateSimCards(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [
                                                Permissions.SIM_ACTIVATE.name,
                                                Permissions.SIM_PAYVIEW_OFFER_LIST.name
                                            ]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    {
                                        isDropdown: true,
                                        trigger: {
                                            icon: ICONS.SIM_CARDS_REASSIGN,
                                            entity: 'actions.row.manage_assign.title',
                                            namespace: nsSimCard,
                                            disabled: !hasRequiredPermissions({
                                                permissions: permissions,
                                                requiredPermissions: [
                                                    Permissions.SIM_REASSIGN.name
                                                ]
                                            }),
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        },
                                        visible: true,
                                        subActions: [
                                            {
                                                entity: 'actions.row.manage_assign.reassign',
                                                namespace: nsSimCard,
                                                onClick: () => setShowModalReassignSimCards(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.SIM_REASSIGN.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            {
                                                entity: 'actions.row.manage_assign.unassign',
                                                namespace: nsSimCard,
                                                onClick: () => setShowModalUnassignSimCards(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.SIM_REASSIGN.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }
                                        ]
                                    }
                                ]}
                                    retractable
                                />
                            }
                        />
                    </TabsContent>
                    <TabsContent tab={SIM_CARDS_TABS.SIM_GROUPS} permissions={permissions}>
                        <SimGroupsTable filtersEventName={setFiltersEventName('sim_groups')} />
                    </TabsContent>
                </Page.Body>
            </Tabs>
            {
                showModalAddSimCards &&
                <CustomPayModalAddSimCards
                    isOpen={showModalAddSimCards}
                    setIsOpen={setShowModalAddSimCards}
                    setLaunchRefetch={storeRefetchSimCards}
                    simProviders={simProviders}
                    isLoading={isLoading}
                />
            }
            {
                showModalAssignSimCards &&
                <CustomPayModalAssignSimCards
                    isOpen={showModalAssignSimCards}
                    setIsOpen={setShowModalAssignSimCards}
                    setLaunchRefetch={storeRefetchSimCards}
                />
            }
            {
                showModalReassignSimCards &&
                <CustomPayModalReassignSimCards
                    isOpen={showModalReassignSimCards}
                    setIsOpen={setShowModalReassignSimCards}
                    setLaunchRefetch={storeRefetchSimCards}
                />
            }
            {
                showModalUnassignSimCards &&
                <CustomPayModalUnassignSimCards
                    isOpen={showModalUnassignSimCards}
                    setIsOpen={setShowModalUnassignSimCards}
                    setLaunchRefetch={storeRefetchSimCards}
                />
            }
            {
                showModalActivateSimCards &&
                <CustomPayModalSimsMultiActivation
                    isOpen={showModalActivateSimCards}
                    setIsOpen={setShowModalActivateSimCards}
                    setLaunchRefetch={storeRefetchSimCards}
                    simProviders={simProviders}
                    isLoading={isLoading}
                />
            }
        </Page>
    );
}

export default CustomPaySimCards;